import * as React from 'react';

import { enumSalesOrderStatus } from '@rematter/frontend-lib-api-client';
import {
  Button,
  cn,
  EntityDetails,
  Loading,
  LocalizedDate,
  Menu,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';
import { MoreHorizontal } from 'lucide-react';

import {
  $currentDraftLineItemsById,
  $effectiveLineItemsById,
  $effectiveNotesById,
  $hasDraftChangesById,
  $optimisticNotesById,
  $remoteSalesOrderActiveLoads,
  $salesOrderDetailById,
  $salesOrderId,
} from '../../_state/salesOrderDetail.state';
import {
  DefaultTranslations,
  useTranslations,
} from '../../i18n/useTranslations';
import { tradeRoutePaths } from '../../routes';
import { SalesOrderDetailsTab, SalesOrderRecord } from '../../types/salesOrder';
import { ContractFreightSection } from '../_shared/ContractFreightSection';
import { SalesOrderStatusBadge } from '../_shared/StatusBadges';
import { LoadCreate } from '../loads/LoadCreate';

import { useCanFinishSalesOrder } from './hooks/useCanFinishSalesOrder';
import { useSalesOrderDetailMenuItems } from './hooks/useSalesOrderDetailMenuItems';
import { SalesOrderTabContractInfo } from './SalesOrderTabContractInfo';
import { SalesOrderTabCustomer } from './SalesOrderTabCustomer';
import { SalesOrderTabDocuments } from './SalesOrderTabDocuments';
import { SalesOrderTabLoads } from './SalesOrderTabLoads';
import { SalesOrderTabMaterials } from './SalesOrderTabMaterials';
import { SalesOrderTabNotes } from './SalesOrderTabNotes';
import { VoidSalesOrderDialog } from './VoidSalesOrderDialog';

import { SalesRootLayout } from '~/modules/trade/_components/_layouts/SalesRootLayout';
import { salesOrderUpdateAtom } from '~/modules/trade/_graphql/mutationSalesOrderUpdate';
import { useAtomFamilyCleanup } from '~/shared/hooks/useAtomFamilyCleanup';
import { useModuleLocation } from '~/shared/hooks/useModuleLocation';

const notificationBannerBySalesOrderStatus = ({
  salesOrder,
  t,
}: {
  salesOrder: SalesOrderRecord | null | undefined;
  t: DefaultTranslations;
}) => {
  if (!salesOrder) return null;

  if (salesOrder.status === enumSalesOrderStatus.Draft) {
    return {
      label: (
        <div className="flex flex-row items-center gap-1">
          {t.salesOrderDetail.notificationDraftBanner()}
        </div>
      ),
      type: 'neutral' as const,
    };
  }

  if (salesOrder.status === enumSalesOrderStatus.Finished) {
    return {
      label: (
        <div className="flex flex-row items-center gap-1">
          {t.salesOrderDetail.notificationBanner({
            status: (
              <span className="font-bold">
                {t.salesOrderDetail.statusLowercase.finished}
              </span>
            ),
            date: (
              <span className="font-bold">
                <LocalizedDate date={salesOrder.timestamps.updatedAt} />
              </span>
            ),
          })}
        </div>
      ),
      type: 'success' as const,
    };
  }

  if (salesOrder.status === enumSalesOrderStatus.Voided) {
    return {
      label: (
        <div className="flex flex-row items-center gap-1">
          {t.salesOrderDetail.notificationBanner({
            status: (
              <span className="font-bold">
                {t.salesOrderDetail.statusLowercase.voided}
              </span>
            ),
            date: (
              <span className="font-bold">
                <LocalizedDate date={salesOrder.timestamps.updatedAt} />
              </span>
            ),
          })}
        </div>
      ),
      type: 'failure' as const,
    };
  }

  return null;
};

export type SalesOrderDetailContentProps = {
  salesOrderId: string;
  variant?: 'default' | 'embedded';
  onBackPressed?: () => void;
  onRefetch?: () => void;
};

export const SalesOrderDetailContent: React.FC<
  SalesOrderDetailContentProps
> = ({ salesOrderId, variant, onBackPressed, onRefetch }) => {
  const toast = useToast();
  const t = useTranslations();
  const [isLoadDialogOpen, setIsLoadDialogOpen] = React.useState(false);
  const [isVoidDialogOpen, setIsVoidDialogOpen] = React.useState(false);
  const [tab, setTab] = React.useState<SalesOrderDetailsTab>('contract-info');

  useAtomFamilyCleanup(
    [
      $salesOrderDetailById,
      $remoteSalesOrderActiveLoads,
      $currentDraftLineItemsById,
      $effectiveLineItemsById,
      $hasDraftChangesById,
      $optimisticNotesById,
      $effectiveNotesById,
    ],
    salesOrderId,
  );

  // CRITICAL: Use atomFamily-based atom - stable reference across renders
  const {
    data: salesOrder,
    isLoading,
    error,
    refetch,
  } = useAtomValue($salesOrderDetailById(salesOrderId));
  const updateSalesOrder = useSetAtom(salesOrderUpdateAtom({}));

  const { canFinish } = useCanFinishSalesOrder({
    salesOrderId,
  });

  const isDisabled = React.useMemo(
    () =>
      salesOrder?.status === enumSalesOrderStatus.Finished ||
      salesOrder?.status === enumSalesOrderStatus.Voided,
    [salesOrder?.status],
  );

  const canRevert = React.useMemo(() => isDisabled, [isDisabled]);

  const handleRefetch = React.useCallback(() => {
    refetch?.();
    onRefetch?.();
  }, [refetch, onRefetch]);

  const notificationBanner = React.useMemo(
    () => notificationBannerBySalesOrderStatus({ salesOrder, t }),
    [salesOrder, t],
  );

  const contractInfoTabContent = React.useMemo(
    () => () =>
      salesOrder ? (
        <SalesOrderTabContractInfo
          salesOrder={salesOrder}
          disabled={isDisabled}
          variant={variant}
        />
      ) : null,
    [salesOrder, isDisabled, variant],
  );

  const materialsTabContent = React.useMemo(
    () => () => (
      <SalesOrderTabMaterials
        salesOrderId={salesOrderId}
        disabled={isDisabled}
        variant={variant}
      />
    ),
    [salesOrderId, isDisabled, variant],
  );

  const customerTabContent = React.useMemo(
    () => () =>
      salesOrder ? (
        <SalesOrderTabCustomer
          salesOrder={salesOrder}
          disabled={isDisabled}
          variant={variant}
          onSaveUpdates={handleRefetch}
        />
      ) : null,
    [salesOrder, isDisabled, variant, handleRefetch],
  );

  const loadsTabContent = React.useMemo(
    () => () =>
      salesOrder ? (
        <SalesOrderTabLoads
          salesOrder={salesOrder}
          disableAddLoad={isDisabled}
          variant={variant}
        />
      ) : null,
    [salesOrder, isDisabled, variant],
  );

  const documentsTabContent = React.useMemo(
    () => () =>
      salesOrder ? (
        <SalesOrderTabDocuments salesOrder={salesOrder} variant={variant} />
      ) : null,
    [salesOrder, variant],
  );

  const freightTabContent = React.useMemo(
    () => () =>
      salesOrder ? (
        <ContractFreightSection
          entityType="salesOrder"
          entityId={salesOrderId}
          disabled={isDisabled}
        />
      ) : null,
    [salesOrder, salesOrderId, isDisabled],
  );

  const notesTabContent = React.useMemo(
    () => () => (
      <SalesOrderTabNotes salesOrderId={salesOrderId} variant={variant} />
    ),
    [salesOrderId, variant],
  );

  const handleVoidConfirm = React.useCallback(
    async (salesOrderIdParam: string, forceUnlink?: boolean) => {
      try {
        const result = await updateSalesOrder({
          id: salesOrderIdParam,
          status: enumSalesOrderStatus.Voided,
          forceUnlink,
        });
        const updateResult = result.tradeSalesOrderUpdate;

        if (updateResult.salesOrder) {
          toast.success(t.salesOrderDetail.messages.voided);
          setIsVoidDialogOpen(false);
          handleRefetch();
        }

        return {
          deleted: !!updateResult.salesOrder,
          blockingLoads: updateResult.blockingLoads?.map((load) => ({
            id: load.id,
            documentNumber: load.documentNumber,
          })),
          unlinkableLoads: updateResult.unlinkableLoads?.map((load) => ({
            id: load.id,
            documentNumber: load.documentNumber,
          })),
        };
      } catch (err) {
        toast.error(
          t.salesOrderDetail.messages.voidError(
            err instanceof Error ? err.message : 'Unknown error',
          ),
        );
        throw err;
      }
    },
    [updateSalesOrder, t, toast, handleRefetch],
  );

  const handleFinish = React.useCallback(async () => {
    if (!salesOrder?.id || !canFinish) return;
    try {
      await updateSalesOrder({
        id: salesOrder.id,
        status: enumSalesOrderStatus.Finished,
      });
      toast.success(t.salesOrderDetail.messages.finished);
      handleRefetch();
    } catch (err) {
      toast.error(
        t.salesOrderDetail.messages.finishError(
          err instanceof Error ? err.message : 'Unknown error',
        ),
      );
    }
  }, [canFinish, salesOrder?.id, updateSalesOrder, t, toast, handleRefetch]);

  const handleRevert = React.useCallback(async () => {
    if (!salesOrder?.id) return;
    try {
      await updateSalesOrder({
        id: salesOrder.id,
        status: enumSalesOrderStatus.Open,
      });
      toast.success(t.salesOrderDetail.messages.reverted);
      handleRefetch();
    } catch (err) {
      toast.error(
        t.salesOrderDetail.messages.revertError(
          err instanceof Error ? err.message : 'Unknown error',
        ),
      );
    }
  }, [salesOrder?.id, updateSalesOrder, t, toast, handleRefetch]);

  const menuItems = useSalesOrderDetailMenuItems({
    salesOrder: salesOrder ?? null,
    setIsLoadDialogOpen,
  });

  const salesOrderActions = React.useMemo(() => {
    if (!salesOrder) return null;

    return (
      <div className="flex items-center gap-2">
        <Menu
          trigger={
            <Button
              icon={<MoreHorizontal className="size-4" />}
              variant="outline"
              className="w-8"
            />
          }
          items={menuItems}
        />
        {salesOrder.status === enumSalesOrderStatus.Open && (
          <>
            <Button
              variant="outline"
              color="failure"
              onPress={() => setIsVoidDialogOpen(true)}
            >
              {t.salesOrderDetail.actions.void}
            </Button>
            <Button
              variant="solid"
              color="primary"
              isDisabled={!canFinish}
              onPress={handleFinish}
            >
              {t.salesOrderDetail.actions.finish}
            </Button>
          </>
        )}
        {canRevert && (
          <Button variant="outline" onPress={handleRevert}>
            {t.salesOrderDetail.actions.revert}
          </Button>
        )}
      </div>
    );
  }, [
    salesOrder,
    menuItems,
    handleFinish,
    handleRevert,
    canRevert,
    canFinish,
    t,
  ]);

  const handleLoadDialogSuccess = React.useCallback(() => {
    setIsLoadDialogOpen(false);
    handleRefetch();
  }, [handleRefetch]);

  const handleCloseLoadDialog = React.useCallback(() => {
    setIsLoadDialogOpen(false);
  }, []);

  if (isLoading) {
    return (
      <div className="p-4">
        <Loading />
      </div>
    );
  }

  if (error || !salesOrder) {
    return (
      <div className="p-4 text-failure">
        {t.salesOrderDetail.error(
          error?.message || t.salesOrderDetail.notFound,
        )}
      </div>
    );
  }

  return (
    <>
      <EntityDetails
        className={cn(
          '@SalesOrderDetailsView',
          'relative overflow-hidden',
          variant === 'embedded' && 'rounded-lg',
        )}
        onChangeTab={(newTab) => setTab(newTab as SalesOrderDetailsTab)}
        currentTab={tab}
        onBackPressed={onBackPressed}
        primaryTitle={t.salesOrderDetail.title(salesOrder.documentNumber)}
        subtitle={
          <>
            {t.salesOrderDetail.created}{' '}
            <LocalizedDate date={salesOrder.timestamps.createdAt} />
          </>
        }
        badges={<SalesOrderStatusBadge status={salesOrder.status} />}
        headerActions={salesOrderActions}
        notificationBanner={notificationBanner}
        tabs={{
          'contract-info': {
            title: t.salesOrderDetail.tabs.contractInfo,
            content: contractInfoTabContent,
          },
          materials: {
            title: t.salesOrderDetail.tabs.materials,
            content: materialsTabContent,
          },
          customer: {
            title: t.salesOrderDetail.tabs.customer,
            content: customerTabContent,
          },
          loads: {
            title: t.salesOrderDetail.tabs.loads,
            content: loadsTabContent,
          },
          documents: {
            title: t.salesOrderDetail.tabs.documents,
            content: documentsTabContent,
          },
          freight: {
            title: t.salesOrderDetail.tabs.freight,
            content: freightTabContent,
          },
          notes: {
            title: t.salesOrderDetail.tabs.notes,
            content: notesTabContent,
          },
        }}
      />
      <LoadCreate
        isOpen={isLoadDialogOpen}
        requiredSalesOrderId={salesOrder?.id}
        onClose={handleCloseLoadDialog}
        onSuccess={handleLoadDialogSuccess}
      />
      {isVoidDialogOpen && salesOrder && (
        <VoidSalesOrderDialog
          salesOrderId={salesOrder.id}
          onVoid={handleVoidConfirm}
          onCancel={() => setIsVoidDialogOpen(false)}
        />
      )}
    </>
  );
};

export const SalesOrderDetailView = () => {
  const t = useTranslations();
  const [, navigate] = useModuleLocation();
  const currentSalesOrderId = useAtomValue($salesOrderId);

  const handleBack = React.useCallback(() => {
    navigate(tradeRoutePaths.salesOrders);
  }, [navigate]);

  if (!currentSalesOrderId) {
    return (
      <SalesRootLayout>
        <div className="p-4 text-failure">
          {t.salesOrderDetail.error(t.salesOrderDetail.notFound)}
        </div>
      </SalesRootLayout>
    );
  }

  return (
    <SalesRootLayout className="@SalesOrderDetailView">
      <SalesOrderDetailContent
        salesOrderId={currentSalesOrderId}
        onBackPressed={handleBack}
      />
    </SalesRootLayout>
  );
};
